import type { InsightDocument, MilestoneStatus } from "../api/insights";

/**
 * The cockpit's readiness view (roadmap R7, R8): the quantum readiness index with its trend and measures, the
 * organisation's own completion deadlines, open policy actions, and the organisational readiness heatmap by area.
 */
const NAVY = "#1F4E79";
const COMPLETION = new Set(["high_priority_complete", "all_complete", "deprecate", "disallow"]);
const KIND: Record<string, string> = { high_priority_complete: "High-priority assets migrated", all_complete: "All assets migrated", deprecate: "Quantum-vulnerable algorithms deprecated", disallow: "Quantum-vulnerable algorithms not allowed" };
const LEVEL = ["Not started", "Started", "Largely in place", "Fully in place"];
const heat = (avg: number) => (avg < 1.75 ? "#F8D7DA" : avg < 2.5 ? "#FFE8CC" : avg < 3.25 ? "#FFF6CC" : "#D9F2E0");
const day = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

export function ExecutiveReadiness({ doc, policyOpen }: { doc: InsightDocument; policyOpen?: number }) {
  const ri = doc.readiness_index;
  const own = new Set(((doc as unknown as { regulatory?: { jurisdictions_selected?: string[] | null } }).regulatory?.jurisdictions_selected) ?? []);
  const today = new Date().toISOString().slice(0, 10);
  const seen = new Set<string>();
  const deadlines = (((doc as unknown as { regulatory?: { milestones?: MilestoneStatus[] } }).regulatory?.milestones) ?? [])
    .filter((m) => own.has(m.jurisdiction) && COMPLETION.has(m.milestone_kind ?? "") && m.date >= today)
    .filter((m) => { const k = `${m.date}|${m.milestone_kind}|${m.scope}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .sort((a, b) => a.date.localeCompare(b.date)).slice(0, 4);
  const areas = (doc.readiness?.areas ?? []).filter((a) => a.avg != null);
  if (!ri && !deadlines.length && !areas.length) return null;
  const delta = ri?.previous ? ri.index - ri.previous.index : null;
  return (
    <section className="grid grid-cols-3 gap-4" aria-label="Readiness" data-testid="executive-readiness">
      {ri && (
        <div className="bg-white border border-ink-100 rounded-sm px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-500">Quantum readiness</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums" style={{ color: NAVY }} data-testid="readiness-index">{ri.index}<span className="text-xs font-normal text-ink-500"> of 100</span></div>
          <div className="text-[11px] text-ink-500">{delta == null ? "First assessment with the index" : `${delta > 0 ? "▲" : delta < 0 ? "▼" : "■"} ${Math.abs(delta)} since the last assessment`}</div>
          <ul className="mt-2 space-y-1">
            {ri.components.filter((c) => c.score != null).map((c) => (
              <li key={c.key} className="text-[11px]" title={`${c.of} · weight ${c.weight}%`}>
                <div className="flex justify-between"><span className="text-ink-700">{c.label}</span><span className="tabular-nums">{Math.round(c.score!)}</span></div>
                <div className="h-1.5 bg-ink-100 rounded"><div className="h-1.5 rounded" style={{ width: `${c.score}%`, background: NAVY }} /></div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {deadlines.length > 0 && (
        <div className="bg-white border border-ink-100 rounded-sm px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-500">Own deadlines</div>
          <ul className="mt-2 space-y-2" data-testid="own-deadlines">
            {deadlines.map((m) => (
              <li key={m.id} className="text-xs">
                <div className="flex justify-between"><span className="font-medium text-ink-800">{day(m.date)}</span><span className={m.assets_projected_to_miss ? "text-red-700" : "text-green-700"}>{m.assets_projected_to_miss} of {m.assets_in_scope} miss</span></div>
                <div className="text-ink-500">{KIND[m.milestone_kind ?? ""] ?? m.milestone_kind} · {m.short_title ?? m.framework}</div>
              </li>
            ))}
          </ul>
          {policyOpen ? <p className="mt-3 text-[11px] text-ink-600">{policyOpen} cryptographic policy actions open in the backlog.</p> : null}
        </div>
      )}
      {areas.length > 0 && (
        <div className="bg-white border border-ink-100 rounded-sm px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-500">Organisational readiness by area</div>
          <div className="mt-2 grid grid-cols-2 gap-1" data-testid="readiness-heatmap">
            {areas.map((a) => (
              <div key={a.category} className="rounded-sm px-2 py-1.5 text-[11px]" style={{ background: heat(a.avg!) }} title={`${a.answered} of ${a.total} answered · average ${a.avg!.toFixed(1)} of 4`}>
                <div className="font-medium text-ink-800">{a.category}</div>
                <div className="text-ink-600">{LEVEL[Math.min(3, Math.max(0, Math.round(a.avg!) - 1))]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
